import api from './api';

export interface ClientPortalUser {
  id: string;
  clientId: number;
  clientName: string;
  name: string;
  email: string;
  role: string;
  accessLevel: 'basic' | 'standard' | 'advanced';
  status: 'active' | 'inactive' | 'suspended' | 'pending';
  lastLogin?: string;
  loginCount: number;
  createdAt: string;
  permissions: {
    viewProjects: boolean;
    viewBilling: boolean;
    downloadFiles: boolean;
    createTickets: boolean;
    viewReports: boolean;
    manageContacts: boolean;
  };
  notifications: {
    projectUpdates: boolean;
    billing: boolean;
    security: boolean;
    marketing: boolean;
  };
  temporaryPassword?: string;
  passwordExpiry?: string;
}

export interface LoginActivity {
  id: string;
  userId: string;
  userName: string;
  action: 'login' | 'logout' | 'failed_login' | 'password_reset' | 'access_denied';
  ipAddress: string;
  userAgent: string;
  timestamp: string;
  success: boolean;
  details?: string;
}

export interface PortalStats {
  totalUsers: number;
  activeUsers: number;
  pendingUsers: number;
  totalLogins: number;
  failedAttempts: number;
  lastMonth: {
    logins: number;
    newUsers: number;
  };
}

class ClientPortalService {
  /**
   * Get all portal users
   */
  async getPortalUsers(): Promise<ClientPortalUser[]> {
    const response = await api.get<{ success: boolean; data: ClientPortalUser[] }>(
      '/admin/clients/portal-users'
    );
    return response.data.data;
  }

  /**
   * Get portal statistics
   */
  async getPortalStats(): Promise<PortalStats> {
    const response = await api.get<{ success: boolean; data: PortalStats }>(
      '/admin/clients/portal-stats'
    );
    return response.data.data;
  }

  /**
   * Get login activity
   */
  async getLoginActivity(params?: { limit?: number }): Promise<LoginActivity[]> {
    const response = await api.get<{ success: boolean; data: LoginActivity[] }>(
      '/admin/clients/portal-activity',
      { params }
    );
    return response.data.data;
  }

  /**
   * Create portal user
   */
  async createPortalUser(data: Partial<ClientPortalUser>): Promise<ClientPortalUser> {
    const response = await api.post<{ success: boolean; data: ClientPortalUser }>(
      '/admin/clients/portal-users',
      data
    );
    return response.data.data;
  }

  /**
   * Update portal user
   */
  async updatePortalUser(id: string, data: Partial<ClientPortalUser>): Promise<ClientPortalUser> {
    const response = await api.put<{ success: boolean; data: ClientPortalUser }>(
      `/admin/clients/portal-users/${id}`,
      data
    );
    return response.data.data;
  }

  /**
   * Delete portal user
   */
  async deletePortalUser(id: string): Promise<void> {
    await api.delete(`/admin/clients/portal-users/${id}`);
  }

  /**
   * Reset user password
   */
  async resetPassword(id: string): Promise<{ temporaryPassword: string; expiresAt: string }> {
    const response = await api.post<{
      success: boolean;
      data: { temporaryPassword: string; expiresAt: string };
    }>(`/admin/clients/portal-users/${id}/reset-password`);
    return response.data.data;
  }

  /**
   * Send credentials email
   */
  async sendCredentials(id: string): Promise<void> {
    await api.post(`/admin/clients/portal-users/${id}/send-credentials`);
  }

  /**
   * Toggle user status
   */
  async toggleStatus(id: string, status: 'active' | 'inactive'): Promise<ClientPortalUser> {
    const response = await api.patch<{ success: boolean; data: ClientPortalUser }>(
      `/admin/clients/portal-users/${id}/status`,
      { status }
    );
    return response.data.data;
  }
}

export default new ClientPortalService();
